import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';

import { BerthReservationStatusDisplayName as StatusName, VesselSideDisplayName as SideToName } from 'server/enums/BerthReservation';
import { BerthReservationVersion } from 'data/models/BerthReservationVersion';
import { CoordinationCardStatuses } from 'data/models/CoordinationCardStatuses';
import { CoordinationCardVersion } from 'data/models/CoordinationCardVersion';
import { Event } from 'data/models/Event';
import { EventType } from 'data/models/EventType';
import { InvitedEditorPortCallChangeAction, LineTimeActualField, WatcherPortCallChangeAction } from 'server/enums/PortCallChange';
import { ItineraryPointVersion } from 'data/models/ItineraryPointVersion';
import { JobOrderVersion } from 'data/models/JobOrderVersion';
import { Measurement, NullableMeasurement } from 'data/models/Measurement';
import { PilotAssignmentChangeAction } from 'server/enums/PilotJobOrderChange';
import { PilotJobOrderCancellation } from 'data/models/PilotJobOrderCancellation';
import { PilotJobOrderChangeType } from 'server/enums/PilotJobOrderChangeType';
import { PilotJobOrderChange } from 'data/models/PilotJobOrderChange';
import { PortCallChange } from 'data/models/PortCallChange';
import { PortCallChangeType } from 'server/enums/PortCallChangeType';
import { PortCallVersion } from 'data/models/PortCallVersion';
import { VesselChange } from 'data/models/VesselChange';
import { VesselChangeType } from 'data/models/VesselChangeType';

import { EventLog, LogEntry, LogEntryComment, CommentRecipient } from './model';

import { CCardStatusIndicator } from 'portcall/ccardstatusindicator/view';
import { DownloadLink } from 'portcall/downloadlink/view';
import { NewCommentView } from './newcommentview';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { TimeAgoTooltip } from 'portcall/pctooltip/view';

export const EventLogView = SuperCritical(({ model: m, allowTaggingUsers } : {
    model : EventLog,
    allowTaggingUsers? : boolean
}) =>
    <div className="eventlogcomponent">
        {m.LogEntries().map(entry =>
            <LogEntryView
                entry={entry}
                now={m.Now}
                allowTaggingUsers={allowTaggingUsers}
                key={entry.Entry.EventId()}
            />
        )}
    </div>
);

const LogEntryView = SuperCritical(({ entry: vm, now, allowTaggingUsers } : {
    entry : LogEntry,
    now : () => TZDate,
    allowTaggingUsers : boolean | undefined
}) =>
    <div className="pc-card">
        <div className="log-entry-main-row">
            <LogEntryIconAndImage entry={vm} />
            <LogEntryContent entry={vm} />
        </div>
        <div className="log-entry-metadata-row clearfix">
            <LogEntryCommentsSummary entry={vm} />
            <LogEntryTimestamp entry={vm} now={now} />
        </div>
        <LogEntryNewCommentView entry={vm} allowTaggingUsers={allowTaggingUsers} />
        <LogEntryCommentsView entry={vm} />
    </div>
);

const LogEntryIconAndImage = SuperCritical(({ entry: { Entry: entry } } : { entry : LogEntry }) => {
    const imageUrl = entry.Event() && LogEntryImageUrl(entry.Event()!);
    return (
        <div className="log-entry-icon-and-image">
            <i className={cx("log-entry-icon", LogEntryIconClass(entry.Event()))} />
            {imageUrl && <img className="thumb-img" src={imageUrl} />}
            {entry.Event() && <CCardStatusUpdateIcon event={entry.Event()!} />}
        </div>
    );
});

const LogEntryContent = SuperCritical(({ entry: vm, entry: { Entry: entry } } : { entry : LogEntry }) =>
    <div className="log-entry-content">
        <div className="log-message">{entry.Event() && LogMessage(entry.Event()!)}</div>
        <LogEntryExtraContent entry={vm} />
    </div>
);

const LogEntryCommentsSummary = SuperCritical(({ entry: vm } : { entry : LogEntry }) => {
    const numComments = vm.Comments().length;
    return (
        <div className="pc-text-smalllabel comments-summary">
            <span className="comment-count">
                {numComments} Comment{numComments === 1 ? '' : 's'}
            </span>
            <span className="pc-icon-bullet" />
            {vm.CanComment() &&
                <a href="" onClick={noDefault(vm.ToggleNewComment)}>
                    {vm.NewComment() ? 'Hide' : 'Add'} Comment
                </a>
            }
        </div>
    );
});

const LogEntryTimestamp = SuperCritical(({ entry: { Entry: entry }, now } : { entry : LogEntry, now : () => TZDate }) => {
    const event = entry.Event();
    return (
        <div className="log-entry-timestamp pc-text-smalllabel">
            {event &&
                <TimeAgoTooltip
                    className="pc-text-fixedwidth pc-text-fixedwidth-tooltip"
                    date={event.Date}
                    now={now}
                    useAbbreviatedTimeAgo={true}
                />
            }
        </div>
    );
});

const LogEntryNewCommentView = SuperCritical(({ entry: vm, allowTaggingUsers } : {
    entry : LogEntry,
    allowTaggingUsers : boolean | undefined
}) =>
    vm.NewComment() &&
        <NewCommentView model={vm.NewComment()!} allowTaggingUsers={allowTaggingUsers} />
);

const LogEntryCommentsView = SuperCritical(({ entry: vm } : { entry : LogEntry }) =>
    vm.Comments().length ?
        <div className="log-entry-comments">
            {vm.Comments().map(comment =>
                <LogEntryCommentView comment={comment} key={comment.Comment.Id()} />
            )}
        </div> :
        null
);

const LogEntryCommentView = SuperCritical(({ comment: vm, comment: { Comment: comment } } : { comment : LogEntryComment }) =>
    <div className="log-entry-comment">
        <div className="comment-icon-and-image">
            {
                comment.Event()?.User() ?
                    <img className="thumb-img" src={comment.Event()!.User()!.Image.ThumbUrl()} /> :
                comment.Event()?.Automation() ?
                    <i className="log-entry-icon icon-bollard" /> :
                null
            }
        </div>
        <div className="comment-content">
            <div>
                <span className="comment-username pc-text-accent">{comment.Event()?.ActorName()}</span>
                <span className="pc-text-smalllabel">{comment.Event() && tzmoment(comment.Event()!.Date()).format('MM/DD/YY [at] HH:mm')}</span>
            </div>
            <div className="comment-message">{comment.Message()}</div>
            <CommentRecipientsView comment={vm} />
            <CommentAttachmentView comment={vm} />
        </div>
    </div>
);

const CommentRecipientsView = SuperCritical(({ comment } : { comment : LogEntryComment }) => {
    const numRecipients = comment.Recipients().length;
    // TODO: Or is it better to always show the "Recipients:" label whether or not there are any recipients?
    return numRecipients > 0 ?
        <div className="comment-recipients-shell">
            <span>Recipients:</span>{" "}
            <span className="comment-recipients">
                {comment.Recipients().map((r, i) =>
                    <CommentRecipientView
                        recipient={r}
                        isFinalRecipient={i === numRecipients - 1}
                        key={r.User.Id()}
                    />
                )}
            </span>
        </div> :
        null;
});

const CommentRecipientView = SuperCritical(({ recipient, isFinalRecipient } : { recipient : CommentRecipient, isFinalRecipient : boolean }) =>
    <span>
        <a href="" onClick={noDefault(recipient.ToggleProfile)}>
            {recipient.User.FullName()}{!isFinalRecipient && ", "}
        </a>
        <PersonProfilePopupView modelContainer={recipient.ProfilePopup} />
    </span>
);

const CommentAttachmentView = SuperCritical(({ comment } : { comment : LogEntryComment }) =>
    comment.Comment.AttachmentUrl() ?
        <div className="comment-attachment-shell">
            <DownloadLink href={comment.Comment.AttachmentUrl()!}>
                <i className="fa fa-paperclip" />{" "}
                <span className="comment-attachment-name pc-text-fixedwidth">
                    {comment.Comment.AttachmentFileName()}
                </span>
            </DownloadLink>
        </div> :
        null
);

function LogEntryIconClass(event : Event | null) {
    // TODO: Should review if this does what we want for all event types, especially version events.
    const
        pcc = event?.PortCallChange(),
        comment = event?.Comment();
    return (
        pcc?.Type() === PortCallChangeType.Watcher ? 'icon-watch' :
        pcc?.Type() === PortCallChangeType.InvitedEditor ? 'fa fa-group' :
        pcc?.Type() === PortCallChangeType.AttendingAgent ? '' :
        comment ? '' :
        // Disable the usual icon b/c we'll show a C-Card status indicator instead.
        event?.Type() === EventType.CCardStatusUpdate ? '' :
        'icon-bollard'
    );
}

function LogEntryImageUrl(event : Event) {
    // TODO: Should review if this does what we want for all event types, especially version events.
    const
        pcc = event.PortCallChange(),
        comment = event.Comment();

    return (
        pcc?.Type() === PortCallChangeType.AttendingAgent ? event.User()?.Image.ThumbUrl() :
        comment ? event.User()?.Image.ThumbUrl() :
        undefined
    );
}

const CCardStatusUpdateIcon = SuperCritical(({ event } : { event : Event }) => {
    if (event.Type() === EventType.CCardStatusUpdate) {
        const ccv = event.CoordinationCardVersion();
        return ccv ? <CCardStatusIndicator statusId={ccv.StatusId()} /> : null;
    } else {
        return null;
    }
});

// Extra content for select log entry types, in addition to the main log message.
const LogEntryExtraContent = SuperCritical(({ entry: vm, entry: { Entry: entry } } : { entry : LogEntry }) =>
    vm.Comment() ?
        <div>
            <CommentRecipientsView comment={vm.Comment()!} />
            <CommentAttachmentView comment={vm.Comment()!} />
        </div> :
    entry.Event()?.Type() === EventType.ReservationStatusChanged ?
        <ReservationStatusChangedExtraContent event={entry.Event()!} /> :
    entry.Event()?.Type() === EventType.CCardStatusUpdate ?
        <CCardStatusUpdateExtraContent event={entry.Event()!} /> :
    entry.Event()?.PilotJobOrderCancellation() ?
        <PJOCancellationExtraContent cnx={entry.Event()!.PilotJobOrderCancellation()!} /> :
    null
);

const ReservationStatusChangedExtraContent = SuperCritical(({ event } : { event : Event }) =>
    <AdditionalRemarks remarks={event.BerthReservationVersion()?.CancellationRemarks() ?? null} />
);

const CCardStatusUpdateExtraContent = SuperCritical(({ event } : { event : Event }) =>
    <AdditionalRemarks remarks={event.CoordinationCardVersion()?.StatusReason() ?? null} />
);

const PJOCancellationExtraContent = SuperCritical(({ cnx } : { cnx : PilotJobOrderCancellation }) =>
    <AdditionalRemarks remarks={cnx.Remarks()} />
);

const AdditionalRemarks = SuperCritical(({ remarks } : { remarks : string | null }) =>
    remarks ?
        <div className="additional-remarks-shell">
            <div><label className="remarks-label">Remarks:</label></div>
            <div className="additional-remarks">{remarks}</div>
        </div> :
        null
);

function LogMessage(event : Event) {
    const 
        pcc = event.PortCallChange(),
        comment = event.Comment(),
        pjoChange = event.PilotJobOrderChange(),
        pjoCancellation = event.PilotJobOrderCancellation(),
        geoActivity = event.GeoActivity(),
        vesselChange = event.VesselChange(),
        pcv = event.PortCallVersion(),
        pcrv = event.PortCallReferenceVersion(),
        ipv = event.ItineraryPointVersion(),
        iprv = event.ItineraryPointReferenceVersion(),
        brv = event.BerthReservationVersion(),
        brrv = event.BerthReservationReferenceVersion(),
        jov = event.JobOrderVersion(),
        jorv = event.JobOrderReferenceVersion(),
        ccv = event.CoordinationCardVersion(),
        ccrv = event.CoordinationCardReferenceVersion(),
        actorName = event.ActorName() ?? '';

    const message =
        pcc             ? portCallChangeLogMessage({ pcc, actorName }) :
        comment         ? actorName + ": " + (comment.Message() ?? '') :
        geoActivity     ? geoActivity.Message() :
        pjoChange       ? pilotJobOrderChangeLogMessage({ pjoChange, actorName }) :
        pjoCancellation ? pilotJobOrderCancellationLogMessage({ pjoCancellation, actorName }) :
        vesselChange    ? vesselChangeLogMessage({ vesselChange, actorName }) :
        pcv             ? portCallVersionChangeMessage(event, pcv, pcrv) :
        ipv             ? itineraryPointVersionChangeMessage(event, ipv, iprv) :
        brv             ? berthReservationVersionChangeMessage(event, brv, brrv) :
        jov             ? jobOrderVersionChangeMessage(event, jov, jorv) :
        ccv             ? ccardEventMessage(event, ccv, ccrv) :
        "";
    
    // sometimes we land on the log without all the entities we need to display a message
    // so just report loading in that case
    return message || "Loading ...";
}

function portCallChangeLogMessage({ pcc, actorName } : {
    pcc : PortCallChange,
    actorName : string
}) {
    var portCall = pcc.PortCall(),
        portCallName = portCall?.DisplayVesselIdentifier() ?? '',
        domain = portCall?.Port()?.Domain(),
        systemName = domain?.DisplayableUrl() ?? '',
        berth = pcc.Berth() ?? pcc.NewBerth() ?? pcc.OldBerth(),
        berthName = berth?.Location()?.Name() ?? '';

    switch (pcc.Type()) {
        case PortCallChangeType.Berth:
        case PortCallChangeType.Berth_DEPRECATED_20170306:
            return actorName + " changed the berth for the reservation on " + tzmoment(pcc.FirstLine()).format("MM/DD/YYYY [at] HH:mm") + " to " + berthName + ".";
        case PortCallChangeType.LineTime:
        case PortCallChangeType.LineTime_DEPRECATED_20170308:
            if ((+pcc.OldFirstLine() !== +pcc.NewFirstLine()) && (+pcc.OldLastLine() !== +pcc.NewLastLine())) {
                return actorName + " changed First Line time at " + berthName + " to " + tzmoment(pcc.NewFirstLine()).format("MM/DD/YYYY [at] HH:mm") + " and the Last Line time to " + tzmoment(pcc.NewLastLine()).format("MM/DD/YYYY [at] HH:mm") + ".";
            } else if (+pcc.OldFirstLine() !== +pcc.NewFirstLine()) {
                return actorName + " changed First Line time at " + berthName + " to " + tzmoment(pcc.NewFirstLine()).format("MM/DD/YYYY [at] HH:mm") + ".";
            } else if (+pcc.OldLastLine() !== +pcc.NewLastLine()) {
                return actorName + " changed Last Line time at " + berthName + " to " + tzmoment(pcc.NewLastLine()).format("MM/DD/YYYY [at] HH:mm") + ".";
            } else {
                return actorName + " changed a line time.";
            }
        case PortCallChangeType.Vessel:
            return actorName + " changed the vessel from " + (pcc.OldVessel() ? pcc.OldVessel()!.Name() : 'a reserved length of ' + pcc.OldLength.Num() + ' ' + pcc.OldLength.UOM()) + " to " + (pcc.NewVessel() ? pcc.NewVessel()!.Name() : '') + ".";
        case PortCallChangeType.LineTimeActual:
            if (pcc.Field() == LineTimeActualField.FirstLineActual)
                return actorName + " logged the " + portCallName + " as moored in " + berthName + " on " + tzmoment(pcc.ActualTime()).format("MM/DD/YYYY [at] HH:mm") + " (FirstLine).";
            else
                return actorName + " logged the " + portCallName + " as departed from " + berthName + " at " + tzmoment(pcc.ActualTime()).format("MM/DD/YYYY [at] HH:mm") + " (LastLine).";
        case PortCallChangeType.AttendingAgent:
            return actorName + " made " + (pcc.NewAgent() ? pcc.NewAgent()!.FullName() : '---') + " the Attending Agent.";
        case PortCallChangeType.Footmark:
        case PortCallChangeType.Footmark_DEPRECATED_20170306:
            // TODO: This adds current Length to the historical Footmark, which seems like it'd
            // be more confusing than helpful. Given that both of these events have been replaced
            // by ReservationFootmarkChangedEvents going forward, which DO capture the historical
            // Length, seems like a better option would be to switch back to displaying just the
            // starting footmark here, since that's all these older event types tracked.
            const endmark = pcc.Reservation() && pcc.NewFootmark.add(pcc.Reservation()!.Length);
            return actorName + " changed the footmarks at " + berthName + " to " + pcc.NewFootmark.toString({ places: 0 }) + " and " + (endmark ? endmark.toString({ places: 0 }) : '--') + ".";
        case PortCallChangeType.InvitedEditor:
            return actorName + (pcc.EditorAction() === InvitedEditorPortCallChangeAction.Add ? " added " : " removed ") + (pcc.InvitedEditor() ? pcc.InvitedEditor()!.FullName() : '') + " as an editor.";
        case PortCallChangeType.ShiftAdded:
        case PortCallChangeType.ShiftAdded_DEPRECATED_20161117:
            return actorName + " added a shift at " + berthName + " for " + tzmoment(pcc.FirstLine()).format("MM/DD/YYYY [at] HH:mm") + " until " + tzmoment(pcc.LastLine()).format("MM/DD/YYYY [at] HH:mm" + ".");
        case PortCallChangeType.ReservationStatus:
        case PortCallChangeType.ReservationStatus_DEPRECATED_20161117:
            return "Status for reservation at " + berthName + " on " + tzmoment(pcc.FirstLine()).format("MM/DD/YYYY [at] HH:mm") + " changed to "
                + StatusName[pcc.NewStatus()]
                // TODO: Why the check on Event.Automation here? Doesn't our actorName logic
                // already handle automations? The logic here seems to have the effect that
                // we'll override any automation name w/ the system name.
                + " by " + (pcc.Event()?.Automation() ? systemName : actorName) + ".";
        case PortCallChangeType.Watcher:
            return actorName + (pcc.WatcherAction() === WatcherPortCallChangeAction.Start ? " started " : " stopped ") + "watching this PortCall.";
        case PortCallChangeType.Creation:
        case PortCallChangeType.Creation_DEPRECATED_20170302:
            // TH 03/02/2017 -- Previously didn't track LastLine for CreationPortCallChanges.
            // If we get one of the deprecated types, display our original message (w/o LastLine).
            // Otherwise show FirstLine and LastLine.
            const originalMessage = (portCall?.Requestor()?.FullName() ?? '') + " requested " + berthName + " for " + tzmoment(pcc.FirstLine()).format("MM/DD/YYYY [at] HH:mm");
            if (pcc.Type() === PortCallChangeType.Creation_DEPRECATED_20170302) return originalMessage + ".";
            return originalMessage + " until " + tzmoment(pcc.LastLine()).format("MM/DD/YYYY [at] HH:mm") + ".";
        case PortCallChangeType.SideTo:
        case PortCallChangeType.SideTo_DEPRECATED_20170306:
            return actorName + " changed the pier side to " + SideToName[pcc.NewSide()] + " for the reservation at " + berthName + ".";
        case PortCallChangeType.Barge:
            return actorName + " added the vessel " + (pcc.NewBarge() ? pcc.NewBarge()!.Name() : '') + " to the PortCall.";
        case PortCallChangeType.ApprovalCard_DEPRECATED_20161125:
            return actorName + " updated the " + (pcc.ApprovalCardTitle() || '') + " approval card.";
        default:
            break;
    }
}

function pilotJobOrderCancellationLogMessage({ pjoCancellation, actorName } : {
    pjoCancellation : PilotJobOrderCancellation,
    actorName : string
}) {
    const pit = pjoCancellation.PointInTime();
    return `${actorName} cancelled the pilot job order for ${pit ? pit.DisplayVesselIdentifier() : '--'} at ${pit ? tzmoment(pit.Start.Estimated()).format('MM/DD/YYYY [at] HH:mm') : '--'}`;
}

function pilotJobOrderChangeLogMessage({ pjoChange, actorName } : {
    pjoChange : PilotJobOrderChange,
    actorName : string
}) {
    switch (pjoChange.Type()) {
        case PilotJobOrderChangeType.Creation:
            const locationText = pjoChange.FromLocation() && pjoChange.ToLocation() ?
                pjoChange.FromLocation()!.Berth() ?
                    "leaving "  + pjoChange.FromLocation()!.Name() :
                    "going to " + pjoChange.ToLocation  ()!.Name() :
                "";
            return actorName + " requested a pilot for the " + pjoChange.DisplayVesselIdentifier() + " " + locationText + " at " + tzmoment(pjoChange.StartEstimated()).format("MM/DD/YYYY [at] HH:mm") + ".";
        case PilotJobOrderChangeType.StartEstimated:
            // TH 03/08/2017 -- We only expect StartEstimated changes to show up for IN movements. We suppress the relevance for OUTs and SHIFTs, in favor of showing the LastLine change for the
            // previous Reservation.
            return actorName + " changed the estimated " + startTimeName(pjoChange) + " time to " + tzmoment(pjoChange.NewStartEstimated()).format("MM/DD/YYYY [at] HH:mm") + ".";
        case PilotJobOrderChangeType.StartActual:
            if (pjoChange.NewStartActual() !== null) {
                if (pjoChange.OldStartActual() === null)
                    return actorName + " logged the pilot aboard the " + pjoChange.DisplayVesselIdentifier() + " on " + tzmoment(pjoChange.NewStartActual()!).format("MM/DD/YYYY [at] HH:mm") + ".";
                return actorName + " changed the actual " + startTimeName(pjoChange) + " time to " + tzmoment(pjoChange.NewStartActual()!).format("MM/DD/YYYY [at] HH:mm") + ".";
            }
            return actorName + " removed the actual " + startTimeName(pjoChange) + " time.";
        case PilotJobOrderChangeType.EndActual:
            if (pjoChange.NewEndActual() !== null) {
                if (pjoChange.OldEndActual() === null)
                    return actorName + " logged the pilot job complete on " + tzmoment(pjoChange.NewEndActual()!).format("MM/DD/YYYY [at] HH:mm") + ".";
                return actorName + " changed the actual Pilot Complete time to " + tzmoment(pjoChange.NewEndActual()!).format("MM/DD/YYYY [at] HH:mm") + ".";
            }
            return actorName + " removed the actual Pilot Complete time.";
        case PilotJobOrderChangeType.StartTimeConfirmed:
            return actorName + " " + (pjoChange.NewStartTimeConfirmed() ? `firmed the ${startTimeName(pjoChange)} time.` : `set the ${startTimeName(pjoChange)} time to tentative.`);
        case PilotJobOrderChangeType.DeepDraftConfirmed:
            return actorName + " " + (pjoChange.NewDeepDraftConfirmed() ? 'confirmed the draft.' : 'set the draft to tentative.');
        case PilotJobOrderChangeType.PilotAssignment:
            return actorName + " " + (pjoChange.Action() === PilotAssignmentChangeAction.Add ? `added ${pilotName(pjoChange)} to this job order.` : `removed ${pilotName(pjoChange)} from this job order.`);
        case PilotJobOrderChangeType.PilotConfirmed:
            return actorName + " " + (pjoChange.NewPilotConfirmed() ? `confirmed ${pilotName(pjoChange)}.` : `set ${pilotName(pjoChange)} to tentative.`);
        case PilotJobOrderChangeType.AirDraft:
            return `${actorName} changed the air draft to ${pjoChange.NewAirDraft.toString({ units: true, places: 1 })}.`;
        case PilotJobOrderChangeType.DeepDraft:
            return `${actorName} changed the deep draft to ${pjoChange.NewDeepDraft.toString({ units: true, places: 1 })}.`;
        case PilotJobOrderChangeType.Remarks:
            return actorName + " " + (!pjoChange.NewRemarks() ? "cleared the remarks." : "remarked: " + pjoChange.NewRemarks());
        case PilotJobOrderChangeType.CancelledDate_DEPRECATED_20180517:
            return actorName + " " + (pjoChange.NewCancelledDate() !== null ? 'cancelled' : 'reordered')
                + " the pilot job order for " + pjoChange.DisplayVesselIdentifier() + " at "
                + tzmoment(pjoChange.StartEstimated()).format("MM/DD/YYYY [at] HH:mm") + ".";
        case PilotJobOrderChangeType.TugCompany:
            if (pjoChange.NewTugCompanyId() === null) {
                return actorName + " cleared the tug for this job order.";
            }
            return actorName + ` added ${pjoChange.NewTugCompany() ? pjoChange.NewTugCompany()!.Name() : ''} to this job order.`;
        case PilotJobOrderChangeType.LineHandlingCompany:
            if (pjoChange.NewLineHandlingCompanyId() === null) {
                return actorName + " cleared the line handler for this job order.";
            }
            return actorName + ` added ${pjoChange.NewLineHandlingCompany() ? pjoChange.NewLineHandlingCompany()!.Name() : ''} to this job order.`;
        case PilotJobOrderChangeType.LaborStartTime:
            if (!pjoChange.NewLaborStartTime()) {
                return actorName + " cleared the Labor start time for this job order.";
            }
            return actorName + ` set the Labor start time to ${tzmoment(pjoChange.NewLaborStartTime()!).format("MM/DD/YYYY [at] HH:mm")}.`;
        case PilotJobOrderChangeType.BowThrusterOperational:
            return (
                pjoChange.NewBowThrusterOperational() ? `${actorName} indicated that the bow thruster is working.` :
                pjoChange.NewBowThrusterOperational() === false ? `${actorName} indicated that the bow thruster is not working.` :
                `${actorName} removed the bow thruster indicator.` // set indicator to null
            );
        default:
            break;
    }
}

function vesselChangeLogMessage({ vesselChange, actorName } : {
    vesselChange : VesselChange,
    actorName : string
}) {
    switch (vesselChange.Type()) {
        case VesselChangeType.BowThrusterPresent:
            if (vesselChange.NewBowThrusterPresent()) {
                return `${actorName} indicates a bow thruster on the vessel.`;
            } else if (vesselChange.NewBowThrusterPresent() === false) {
                return `${actorName} indicates there is no bow thruster on the vessel.`;
            } else {
                return `${actorName} cleared the bow thruster indicator.`;
            }
        case VesselChangeType.BowThrusterSize:
            return `${actorName} changed the bow thruster to ${vesselChange.NewBowThrusterSize.toString()}.`;
        default:
            break;
    }
}

function portCallVersionChangeMessage(ev : Event, pcv : PortCallVersion, pcrv : PortCallVersion | null) {
    const
        t = ev.Type(),
        data = pcv.ExtraData(),
        origData = pcrv?.ExtraData(),
        vesselName = pcv.VesselId() ? data?.VesselName : `a requested length of ${fmtM(pcv.LengthOnlyLength)}`,
        origVesselName = pcrv === null ? '' : pcrv.VesselId() ? origData?.VesselName : `a requested length of ${fmtM(pcrv.LengthOnlyLength)}`;

    return (
        EventType.PortCallAgentChanged === t ?
            `${ev.ActorName()} changed the Attending Agent from ${origData ? origData.AgentName : '(loading...)'} to ${data ? data.AgentName : '(loading...)'}.` :
        EventType.PortCallVesselChanged === t ?
            `${ev.ActorName()} changed the vessel from ${origVesselName || '(loading...)'} to ${vesselName || '(loading...)'}` :
        ""
    );
}

function itineraryPointVersionChangeMessage(ev : Event, ipv : ItineraryPointVersion, iprv : ItineraryPointVersion | null) {
    const
        t = ev.Type(),
        data = ipv.ExtraData(),
        origData = iprv?.ExtraData(),
        portCallName = data?.PortCall?.VesselName,
        locationName = data?.LocationName,
        origLocationName = origData?.LocationName,
        firstLine = fmtD(ipv.Arrival.Effective()),
        origFirstLine = iprv && fmtD(iprv.Arrival.Effective()),
        lastLine = fmtD(ipv.Departure.Effective()),
        origLastLine = iprv && fmtD(iprv.Departure.Effective()),
        zeroLength = firstLine === lastLine;

    return (
        EventType.IPLocationChanged === t ?
            `${ev.ActorName()} changed the berth for the reservation on ${firstLine} from ${origLocationName || '(loading...)'} to ${locationName || '(loading...)'}.` :
        EventType.IPArrivalEffDateChanged === t ?
            `${ev.ActorName()} changed the ${zeroLength || 'First Line'} time at ${locationName || '(loading...)'} from ${origFirstLine || '(loading...)'} to ${firstLine}.` :
        EventType.IPDepartureEffDateChanged === t ?
            `${ev.ActorName()} changed the ${zeroLength || 'Last Line'} time at ${locationName || '(loading...)'} from ${origLastLine || '(loading...)'} to ${lastLine}.` :
        EventType.IPBothEffDatesChanged === t && zeroLength ?
            `${ev.ActorName()} changed the time at ${locationName || '(loading...)'} from ${origLastLine || '(loading...)'} to ${lastLine}.` :
        EventType.IPBothEffDatesChanged === t ?
            `${ev.ActorName()} changed the First Line time at ${locationName || '(loading...)'} from ${origFirstLine || '(loading...)'} to ${firstLine} and the Last Line time from ${origLastLine || '(loading...)'} to ${lastLine}.` :
        EventType.IPArrivalLogged === t ?
            `${ev.ActorName()} logged the ${portCallName || '(loading...)'} as moored in ${locationName || '(loading...)'} on ${firstLine} (First Line).` :
        EventType.IPDepartureLogged === t ?
            `${ev.ActorName()} logged the ${portCallName || '(loading...)'} as departed from ${locationName || '(loading...)'} at ${lastLine} (Last Line).` :
        EventType.IPArrivalUnlogged === t && firstLine !== origFirstLine ?
            `${ev.ActorName()} undid the actual First Line time at ${locationName || '(loading...)'}.  Estimated First Line now ${firstLine}.` :
        EventType.IPArrivalUnlogged === t ?
            `${ev.ActorName()} undid the actual First Line time at ${locationName || '(loading...)'}.` :
        EventType.IPDepartureUnlogged === t && lastLine !== origLastLine ?
            `${ev.ActorName()} undid the actual Last Line time at ${locationName || '(loading...)'}.  Estimated Last Line now ${lastLine}.` :
        EventType.IPDepartureUnlogged === t ?
            `${ev.ActorName()} undid the actual Last Line time at ${locationName || '(loading...)'}.` :
        ""
    );
}

function berthReservationVersionChangeMessage(ev : Event, brv : BerthReservationVersion, brrv : BerthReservationVersion | null) {
    const 
        t = ev.Type()!,
        data = brv.ExtraData(),
        pt = data?.ItineraryPoint,
        locationName = pt?.LocationName,
        firstLine = pt && fmtD(pt.Arrival.Effective),
        footmark = fmtM(brv.Footmark),
        origFootmark = brrv && fmtM(brrv.Footmark),
        endmark = fmtM(brv.Footmark.add(brv.Length)),
        origEndmark = brrv && fmtM(brrv.Footmark.add(brrv.Length));

    return (
        EventType.ReservationStatusChanged === t ? 
            `Status for reservation at ${locationName || '(loading...)'} on ${firstLine || '(loading...)'} changed from ${brrv ? StatusName[brrv.Status()] : '(loading...)'} to ${StatusName[brv.Status()]} by ${ev.ActorName()}.` :
        EventType.ReservationSideToChanged === t ?
            `${ev.ActorName()} changed the pier side from ${brrv ? SideToName[brrv.SideTo()] : '(loading...)'} to ${SideToName[brv.SideTo()]} for the reservation at ${locationName || '(loading...)'}.` :
        EventType.ReservationFootmarkChanged === t ?
            `${ev.ActorName()} changed the footmark at ${locationName || '(loading...)'} from ${origFootmark || '(loading...)'} to ${footmark} and endmark from ${origEndmark || '(loading...)'} to ${endmark || '(loading...)'}.` :
        ""
    );
}

function ccardEventMessage(ev : Event, ccv : CoordinationCardVersion, ccrv : CoordinationCardVersion | null) {
    const
        t = ev.Type()!,
        actorName = ev.ActorAbbreviatedName();

    return (
        EventType.CCardCancelled === t ?
            `Card cancelled by ${actorName}.` :
        EventType.CCardProviderChanged === t ?
            `Provider changed from ${ccrv?.Provider()?.Provider()?.Name() ?? '(loading...)'} to ${ccv.Provider()?.Provider()?.Name() ?? '(loading...)'} by ${actorName}.` :
        EventType.CCardRequested === t ?
            // TODO: Checking ev.ReferenceId() would let us distinguish brand new coordination (ev.ReferenceId()
            // === null) vs cancelled-then-re-requested coordination (ev.ReferenceId() !== null). Do we want
            // slightly different text in those two cases? 
            `Coordination with ${ccv.Provider()?.Provider()?.Name() ?? '(loading...)'} requested by ${actorName}.` :
        EventType.CCardStatusUpdate === t ?
            ccardStatusUpdateMessage(ev, ccv) :
        ''
    );
}

function ccardStatusUpdateMessage(ev : Event, ccv : CoordinationCardVersion) {
    // NOTE: A status update event does NOT necessarily represent a CHANGE in status. Can also mean a user
    // reconfirmed the existing status (possibly with a new status reason, but not necessarily) or that multiple
    // upstream events in a row triggered a status reset. So avoiding language like "status changed from blah to
    // blah."
    const actorName = ev.ActorAbbreviatedName();
    if (ccv.StatusId() === CoordinationCardStatuses.Pending) {
        const
            cause = ev.CCardStatusUpdateCause(),
            causeText = cause.Type() === EventType.CCardProviderChanged ?
                'Provider changed' :
                'Milestone updated';
        return `${causeText} by ${actorName}. ${ccv.EffectiveStatusLabel(true)} from ${ccv.Provider()?.Provider()?.Name() ?? '(loading...)'}.`;
    } else {
        return `${ccv.EffectiveStatusLabel()} by ${actorName}.`
    }
}

function jobOrderVersionChangeMessage(ev : Event, jov : JobOrderVersion, jorv : JobOrderVersion | null) {
    return "jov change message";
}

// TH 03/08/2017 -- We use different language to describe the start time of IN movements
// vs SHIFT and OUT.
function startTimeName(pjoChange : PilotJobOrderChange) {
    const isINMvmt = !!pjoChange.PilotJobOrder()?.FromPoint()
        && pjoChange.PilotJobOrder()!.FromPoint()!.BerthReservation() === null;
    return isINMvmt ? 'Arrival' : 'Departure';
}

function pilotName(pjoChange : PilotJobOrderChange) {
    // TH 03/01/2017 -- TODO: How do they want the Pilot name displayed?
    // Given that they don't wanna even show the PilotAssignments widget for non-Pilots/Dispatchers,
    // are they comfortable displaying the full Pilot name in the Log?
    return pjoChange.Pilot()?.User()?.FullName() ?? '';
}

function fmtD(d : TZDate | string) {
    return tzmoment(d).format("MM/DD/YYYY [at] HH:mm");
}

function fmtM(m : NullableMeasurement) {
    return m.toString({ places: 0 });
}



// WEBPACK FOOTER //
// ./apps/portcall/eventlog/view.tsx